/**
 * Schema formatting utilities for NewEntityFilters component
 * Extracted from the main component to improve modularity and testability
 */
import { CROSS_FILTER_ID_SEPARATOR, getCrossID } from 'utils/filters';

/**
 * Formats the final schema for standard filters
 * @param {Object} config - Configuration object
 * @param {Array} config.visibleFilters - Array of visible filter IDs
 * @param {Array} config.quickFilters - Array of quick filter IDs
 * @param {Object} config.mappedSchema - Mapped schema object
 * @param {Array} config.hiddenFilters - Array of hidden filter IDs
 * @param {Function} config.getFieldsFormatted - Function to format fields
 * @param {Function} config.getSubfilterSchema - Function to get subfilter schema
 * @param {Array<string>} [config.readOnlyFilterIds] - Filter ids read-only in QuickFilters and Filters UI
 * @returns {Array} Formatted schema array
 */
export const formatFinalSchema = ({
    visibleFilters,
    quickFilters,
    mappedSchema,
    hiddenFilters,
    getFieldsFormatted,
    getSubfilterSchema,
    readOnlyFilterIds = [],
}) => {
    if (!visibleFilters?.length) return [];

    return visibleFilters
        .filter((filter) => !quickFilters.includes(filter))
        .reduce((arr, current) => {
            if (!mappedSchema?.[current]) return arr;
            if (hiddenFilters.includes(current)) return arr;

            const mappedSchemaItem = mappedSchema[current];

            const isReadOnlyFilter = readOnlyFilterIds.includes(current);

            let isRemovable = true;
            if (mappedSchemaItem?.hasOwnProperty('isRemovable')) {
                isRemovable = mappedSchemaItem.isRemovable;
            }
            let isPinnable = true;
            if (mappedSchemaItem?.hasOwnProperty('isPinnable')) {
                isPinnable = mappedSchemaItem.isPinnable;
            }

            if (isReadOnlyFilter) {
                isRemovable = false;
                isPinnable = false;
            }

            let subfilterSchema;
            if (mappedSchemaItem?.hasOwnProperty('subfilterKey')) {
                subfilterSchema = getSubfilterSchema({
                    filter: current,
                    key: mappedSchemaItem.subfilterKey,
                    schema: mappedSchema,
                });
            }

            const formattedFields = [...getFieldsFormatted(mappedSchemaItem)];
            const fields = isReadOnlyFilter
                ? formattedFields.map((f) => ({ ...f, isFilterReadOnly: true }))
                : formattedFields;

            arr.push({
                label: mappedSchemaItem.description,
                id: mappedSchemaItem.id,
                filterTooltip: mappedSchemaItem.filterTooltip,
                fields,
                isRemovable,
                isPinnable,
                isReadOnly: isReadOnlyFilter,
                subfilterSchema,
            });

            return arr;
        }, []);
};

/**
 * Formats the final schema for quick filters
 * @param {Object} config - Configuration object
 * @param {Array} config.quickFilters - Array of quick filter IDs
 * @param {Object} config.mappedSchema - Mapped schema object
 * @param {Object} config.crossSchema - Cross schema object
 * @param {Array} config.hiddenFilters - Array of hidden filter IDs
 * @param {Function} config.getFieldsFormatted - Function to format fields
 * @param {Function} config.getSubfilterSchema - Function to get subfilter schema
 * @param {Object} config.crossOperators - Cross operators object
 * @param {Array<string>} [config.readOnlyFilterIds] - Same ids locked in QuickFilters and Filters
 * @returns {Array} Formatted quick schema array
 */
export const formatFinalQuickSchema = ({
    quickFilters,
    mappedSchema,
    crossSchema,
    hiddenFilters,
    getFieldsFormatted,
    getSubfilterSchema,
    crossOperators,
    readOnlyFilterIds = [],
}) => {
    if (!quickFilters || quickFilters.length === 0) return [];

    return quickFilters.reduce((arr, current) => {
        const splitFilter = current.split(CROSS_FILTER_ID_SEPARATOR);
        const isCrossFilter = splitFilter.length > 1;

        let crossEntity, crossID, crossEntityOperators;

        if (isCrossFilter) {
            [crossEntity, crossID] = splitFilter;
            crossEntityOperators = crossOperators?.[crossEntity] || {};
        }

        const finalMappedSchema = isCrossFilter
            ? crossSchema[crossEntity]?.mappedSchema
            : mappedSchema;

        const finalCurrent = isCrossFilter ? crossID : current;

        if (!finalMappedSchema?.[finalCurrent]) return arr;
        if (hiddenFilters.includes(finalCurrent)) return arr;

        let isRemovable = true;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isRemovable')) {
            isRemovable = finalMappedSchema[finalCurrent].isRemovable;
        }

        let isPinnable = true;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isPinnable')) {
            isPinnable = finalMappedSchema[finalCurrent].isPinnable;
        }

        let isFakeCrossFilter = false;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isFakeCrossFilter')) {
            isFakeCrossFilter = finalMappedSchema[finalCurrent].isFakeCrossFilter;
        }

        let isReadOnly = false;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('isReadOnly')) {
            isReadOnly = finalMappedSchema[finalCurrent].isReadOnly;
        }
        if (readOnlyFilterIds.includes(finalCurrent)) {
            isReadOnly = true;
        }

        if (isReadOnly) {
            isRemovable = false;
            isPinnable = false;
        }

        let subfilterSchema;
        if (finalMappedSchema[finalCurrent]?.hasOwnProperty('subfilterKey')) {
            subfilterSchema = getSubfilterSchema({
                crossEntity,
                filter: finalCurrent,
                key: finalMappedSchema[finalCurrent].subfilterKey,
                schema: finalMappedSchema,
            });
        }

        const formattedQuickFields = [
            ...getFieldsFormatted(finalMappedSchema[finalCurrent], crossEntityOperators),
        ];
        const fields = isReadOnly
            ? formattedQuickFields.map((f) => ({ ...f, isFilterReadOnly: true }))
            : formattedQuickFields;

        const filterObj = {
            label: finalMappedSchema[finalCurrent].description,
            id: finalMappedSchema[finalCurrent].id,
            filterTooltip: finalMappedSchema[finalCurrent].filterTooltip,
            fields,
            isRemovable,
            isPinnable,
            isFakeCrossFilter,
            isReadOnly,
            subfilterSchema,
        };

        if (isCrossFilter) {
            filterObj.subLabel = crossSchema[crossEntity]?.title;
            filterObj.entity = crossEntity;
            filterObj.crossId = getCrossID(finalMappedSchema[finalCurrent].id, crossEntity);
        }

        arr.push(filterObj);

        return arr;
    }, []);
};

/**
 * Formats the final cross schema
 * @param {Object} config - Configuration object
 * @param {Object} config.crossSchema - Cross schema object
 * @param {Function} config.getFinalCrossSchema - Function to get final cross schema
 * @param {Array} config.quickfilters - Mapped quick filters
 * @param {Array} config.visibleFilters - Mapped visible filters
 * @param {Function} config.getFieldsFormatted - Function to format fields
 * @param {Function} config.getSubfilterSchema - Function to get subfilter schema
 * @returns {Object} Formatted cross schema object
 */
export const formatFinalCrossSchema = ({
    crossSchema,
    getFinalCrossSchema,
    quickfilters,
    visibleFilters,
    getFieldsFormatted,
    getSubfilterSchema,
}) => {
    if (!crossSchema || Object.entries(crossSchema).length === 0) return {};

    return getFinalCrossSchema({
        crossSchema,
        quickfilters,
        visibleFilters,
        getFieldsFormatted,
        getSubfilterSchema,
    });
};
